import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// Supported languages. Order defines display order in the switcher.
export const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "fr", label: "Français", short: "FR" },
  { code: "es", label: "Español", short: "ES" },
];

const STORAGE_KEY = "bbt.lang";

// Flat-key translation dictionary. Keep keys stable and unique.
// Missing keys fall back to English, then to the raw key.
const TRANSLATIONS = {
  en: {
    // Navbar
    "nav.home": "Home",
    "nav.services": "Services",
    "nav.destinations": "Destinations",
    "nav.corporate": "Corporate Solutions",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.getQuote": "Get a Quote",
    "nav.language": "Language",

    // Quote branch modal
    "quote.modal.title": "Choose your branch",
    "quote.modal.subtitle": "Select a country and we'll connect you with the right team on WhatsApp.",
    "quote.modal.chatWith": "Chat with our {country} team on WhatsApp",
    "quote.modal.close": "Close",

    // Hero — home
    "hero.eyebrow": "Beyond Borders Travel · Lilongwe → Worldwide",
    "hero.title1": "Corporate Travel That",
    "hero.titleAccent": "Moves",
    "hero.title2": "Your Business",
    "hero.title3": "Forward.",
    "hero.subtitle":
      "Effortless, cost-efficient and globally connected travel solutions tailored for modern businesses. A decade of navigating skies, suites and schedules — so you don't have to.",
    "hero.ctaQuote": "Get a Quote",
    "hero.ctaServices": "Explore Services",
    "hero.scroll": "scroll",
    "hero.stat.years": "Years of expertise",
    "hero.stat.countries": "Countries served",
    "hero.stat.retention": "Client retention",
    "hero.stat.support": "Traveller support",

    // WhatsApp widget
    "wa.tooltip": "Chat with us",
    "wa.aria": "Chat with us on WhatsApp",

    // Footer
    "footer.taglineExtra":
      "From Lilongwe and Malabo to anywhere — we orchestrate every detail of premium corporate and leisure travel.",
    "footer.explore": "Explore",
    "footer.services": "Services",
    "footer.ourBranches": "Our Branches",
    "footer.currentBranches": "Current Branches",
    "footer.upcomingBranches": "Upcoming Branches",
    "footer.copyright": "All rights reserved.",
    "footer.crafted": "Crafted with discretion · Lilongwe · Malabo · Worldwide",

    // Contact page
    "contact.hero.eyebrow": "Contact",
    "contact.hero.title": "Tell us where",
    "contact.hero.titleAccent": "you're going.",
    "contact.hero.subtitle":
      "We'll handle the how, the where, the when — and the unexpected.",

    "contact.branches.eyebrow": "Currently operating",
    "contact.branches.title": "Our branches.",
    "contact.branches.subtitle":
      "Two doors, one family. Reach the office nearest you — or send a single enquiry and the closest team will pick it up.",
    "contact.branch.openNow": "Open now",
    "contact.branch.office": "Office",
    "contact.branch.phone": "Phone",
    "contact.branch.email": "Email",
    "contact.branch.hours": "Hours",
    "contact.branch.chat": "Chat with {city} on WhatsApp",

    "contact.form.title": "Request a quote",
    "contact.form.intro":
      "Fill in your details once — then send via WhatsApp or Email. Routed to the branch you choose.",
    "contact.form.fullName": "Full name",
    "contact.form.email": "Email",
    "contact.form.phone": "Phone",
    "contact.form.message": "Tell us about your trip",
    "contact.form.messagePlaceholder":
      "Two travellers · Lilongwe → Dubai · 4–8 March · Business class · 5★ hotel near DIFC",
    "contact.form.branch": "Preferred branch",
    "contact.form.branchAuto": "Auto — nearest team",
    "contact.form.submit": "Send Enquiry",
    "contact.form.submitting": "Opening WhatsApp...",
    "contact.form.emailBtn": "Send via Email",
    "contact.form.whatsappBtn": "Send via WhatsApp",
    "contact.form.errNameEmailMessage":
      "Please fill in your name, email and message.",
    "contact.form.errValidEmail": "Please enter a valid email.",
    "contact.form.successWA": "Opening WhatsApp — your enquiry is ready to send.",
    "contact.form.successEmail":
      "Opening your email client — your enquiry is ready to send.",
    "contact.form.successDesc":
      "We'll respond within minutes during business hours.",

    "contact.upcoming.eyebrow": "Expanding Beyond Borders",
    "contact.upcoming.title": "Our upcoming branches.",
    "contact.upcoming.subtitle":
      "Future expansion locations — planned offices, not yet operational. Coming soon.",
    "contact.upcoming.badge": "Coming soon",
    "contact.upcoming.card": "Upcoming branch",
    "contact.upcoming.note":
      "Contact any of our currently operating branches above for enquiries. We'll announce opening dates for new locations here.",

    "contact.maps.eyebrow": "Find us",
    "contact.maps.title": "Two doors. One family.",
    "contact.maps.titleAccent": "One family.",

    // Page hero headings (short)
    "page.services.eyebrow": "Our Services",
    "page.services.title1": "Eight services.",
    "page.services.title2": "One trusted desk.",
    "page.services.subtitle":
      "Every detail of your journey — orchestrated by a single team that never closes.",

    "page.destinations.eyebrow": "Destinations",
    "page.destinations.title1": "Inspiration for the",
    "page.destinations.title2": "well-travelled.",
    "page.destinations.subtitle":
      "From boardrooms in Dubai to overwater villas in the Maldives — our most-requested destinations, pre-vetted by the team.",

    "page.about.eyebrow": "About Us",
    "page.about.title1": "A travel desk built on",
    "page.about.title2": "discretion.",

    "page.corporate.eyebrow": "Corporate Suite",
    "page.corporate.title1": "Travel programmes built for",
    "page.corporate.title2": "growing enterprises.",
  },

  fr: {
    "nav.home": "Accueil",
    "nav.services": "Services",
    "nav.destinations": "Destinations",
    "nav.corporate": "Solutions Entreprise",
    "nav.about": "À propos",
    "nav.contact": "Contact",
    "nav.getQuote": "Obtenir un devis",
    "nav.language": "Langue",

    "quote.modal.title": "Choisissez votre agence",
    "quote.modal.subtitle": "Sélectionnez un pays et nous vous mettrons en relation avec la bonne équipe sur WhatsApp.",
    "quote.modal.chatWith": "Discuter avec notre équipe {country} sur WhatsApp",
    "quote.modal.close": "Fermer",

    "hero.eyebrow": "Beyond Borders Travel · Lilongwe → International",
    "hero.title1": "Le voyage d'affaires qui",
    "hero.titleAccent": "propulse",
    "hero.title2": "votre entreprise",
    "hero.title3": "vers l'avant.",
    "hero.subtitle":
      "Des solutions de voyage fluides, économiques et connectées à l'échelle mondiale, taillées pour les entreprises modernes. Dix ans à naviguer les cieux, les suites et les agendas — pour que vous n'ayez pas à le faire.",
    "hero.ctaQuote": "Obtenir un devis",
    "hero.ctaServices": "Découvrir nos services",
    "hero.scroll": "défiler",
    "hero.stat.years": "Ans d'expertise",
    "hero.stat.countries": "Pays desservis",
    "hero.stat.retention": "Rétention client",
    "hero.stat.support": "Support voyageur",

    "wa.tooltip": "Discuter avec nous",
    "wa.aria": "Discuter avec nous sur WhatsApp",

    "footer.taglineExtra":
      "De Lilongwe et Malabo vers le monde entier — nous orchestrons chaque détail de votre voyage d'affaires ou de loisir premium.",
    "footer.explore": "Explorer",
    "footer.services": "Services",
    "footer.ourBranches": "Nos agences",
    "footer.currentBranches": "Agences actuelles",
    "footer.upcomingBranches": "Agences à venir",
    "footer.copyright": "Tous droits réservés.",
    "footer.crafted": "Conçu avec discrétion · Lilongwe · Malabo · International",

    "contact.hero.eyebrow": "Contact",
    "contact.hero.title": "Dites-nous où",
    "contact.hero.titleAccent": "vous allez.",
    "contact.hero.subtitle":
      "Nous gérons le comment, le où, le quand — et l'imprévu.",

    "contact.branches.eyebrow": "Actuellement en activité",
    "contact.branches.title": "Nos agences.",
    "contact.branches.subtitle":
      "Deux portes, une même famille. Contactez le bureau le plus proche — ou envoyez une seule demande, l'équipe la plus proche la traitera.",
    "contact.branch.openNow": "Ouvert",
    "contact.branch.office": "Bureau",
    "contact.branch.phone": "Téléphone",
    "contact.branch.email": "E-mail",
    "contact.branch.hours": "Horaires",
    "contact.branch.chat": "Discuter avec {city} sur WhatsApp",

    "contact.form.title": "Demander un devis",
    "contact.form.intro":
      "Remplissez vos coordonnées une seule fois — puis envoyez via WhatsApp ou e-mail. Acheminé vers l'agence de votre choix.",
    "contact.form.fullName": "Nom complet",
    "contact.form.email": "E-mail",
    "contact.form.phone": "Téléphone",
    "contact.form.message": "Parlez-nous de votre voyage",
    "contact.form.messagePlaceholder":
      "Deux voyageurs · Lilongwe → Dubaï · 4–8 mars · Classe affaires · Hôtel 5★ près du DIFC",
    "contact.form.branch": "Agence préférée",
    "contact.form.branchAuto": "Automatique — équipe la plus proche",
    "contact.form.submit": "Envoyer la demande",
    "contact.form.submitting": "Ouverture de WhatsApp...",
    "contact.form.emailBtn": "Envoyer par e-mail",
    "contact.form.whatsappBtn": "Envoyer via WhatsApp",
    "contact.form.errNameEmailMessage":
      "Veuillez renseigner votre nom, votre e-mail et votre message.",
    "contact.form.errValidEmail": "Veuillez saisir un e-mail valide.",
    "contact.form.successWA":
      "Ouverture de WhatsApp — votre demande est prête à être envoyée.",
    "contact.form.successEmail":
      "Ouverture de votre client e-mail — votre demande est prête à être envoyée.",
    "contact.form.successDesc":
      "Nous répondons en quelques minutes pendant les heures ouvrées.",

    "contact.upcoming.eyebrow": "Nous nous développons",
    "contact.upcoming.title": "Nos agences à venir.",
    "contact.upcoming.subtitle":
      "Futures implantations — bureaux planifiés, pas encore opérationnels. Bientôt disponibles.",
    "contact.upcoming.badge": "Bientôt",
    "contact.upcoming.card": "Agence à venir",
    "contact.upcoming.note":
      "Contactez l'une de nos agences actuellement en activité pour toute demande. Les dates d'ouverture des nouveaux bureaux seront annoncées ici.",

    "contact.maps.eyebrow": "Nous trouver",
    "contact.maps.title": "Deux portes. Une famille.",
    "contact.maps.titleAccent": "Une famille.",

    "page.services.eyebrow": "Nos services",
    "page.services.title1": "Huit services.",
    "page.services.title2": "Un seul bureau de confiance.",
    "page.services.subtitle":
      "Chaque détail de votre voyage — orchestré par une équipe qui ne ferme jamais.",

    "page.destinations.eyebrow": "Destinations",
    "page.destinations.title1": "Inspiration pour les",
    "page.destinations.title2": "grands voyageurs.",
    "page.destinations.subtitle":
      "Des salles de conseil à Dubaï aux villas sur pilotis aux Maldives — nos destinations les plus demandées, sélectionnées par notre équipe.",

    "page.about.eyebrow": "À propos",
    "page.about.title1": "Un bureau de voyage bâti sur la",
    "page.about.title2": "discrétion.",

    "page.corporate.eyebrow": "Suite Entreprise",
    "page.corporate.title1": "Programmes de voyage pour",
    "page.corporate.title2": "entreprises en croissance.",
  },

  es: {
    "nav.home": "Inicio",
    "nav.services": "Servicios",
    "nav.destinations": "Destinos",
    "nav.corporate": "Soluciones Corporativas",
    "nav.about": "Nosotros",
    "nav.contact": "Contacto",
    "nav.getQuote": "Solicitar cotización",
    "nav.language": "Idioma",

    "quote.modal.title": "Elija su oficina",
    "quote.modal.subtitle": "Seleccione un país y le conectaremos con el equipo adecuado en WhatsApp.",
    "quote.modal.chatWith": "Chatee con nuestro equipo de {country} por WhatsApp",
    "quote.modal.close": "Cerrar",

    "hero.eyebrow": "Beyond Borders Travel · Lilongwe → Mundial",
    "hero.title1": "El viaje corporativo que",
    "hero.titleAccent": "impulsa",
    "hero.title2": "su negocio",
    "hero.title3": "hacia adelante.",
    "hero.subtitle":
      "Soluciones de viaje fluidas, rentables y globalmente conectadas, hechas a medida para empresas modernas. Una década navegando cielos, suites y agendas — para que usted no tenga que hacerlo.",
    "hero.ctaQuote": "Solicitar cotización",
    "hero.ctaServices": "Explorar servicios",
    "hero.scroll": "desplazar",
    "hero.stat.years": "Años de experiencia",
    "hero.stat.countries": "Países atendidos",
    "hero.stat.retention": "Retención de clientes",
    "hero.stat.support": "Soporte al viajero",

    "wa.tooltip": "Chatee con nosotros",
    "wa.aria": "Chatee con nosotros por WhatsApp",

    "footer.taglineExtra":
      "De Lilongwe y Malabo a cualquier parte — orquestamos cada detalle de sus viajes corporativos y de placer premium.",
    "footer.explore": "Explorar",
    "footer.services": "Servicios",
    "footer.ourBranches": "Nuestras oficinas",
    "footer.currentBranches": "Oficinas actuales",
    "footer.upcomingBranches": "Próximas oficinas",
    "footer.copyright": "Todos los derechos reservados.",
    "footer.crafted": "Elaborado con discreción · Lilongwe · Malabo · Mundial",

    "contact.hero.eyebrow": "Contacto",
    "contact.hero.title": "Cuéntenos a dónde",
    "contact.hero.titleAccent": "viaja.",
    "contact.hero.subtitle":
      "Nos ocupamos del cómo, del dónde, del cuándo — y de lo imprevisto.",

    "contact.branches.eyebrow": "Actualmente operando",
    "contact.branches.title": "Nuestras oficinas.",
    "contact.branches.subtitle":
      "Dos puertas, una misma familia. Contacte a la oficina más cercana — o envíe una única consulta, y el equipo más próximo la atenderá.",
    "contact.branch.openNow": "Abierto",
    "contact.branch.office": "Oficina",
    "contact.branch.phone": "Teléfono",
    "contact.branch.email": "Correo",
    "contact.branch.hours": "Horario",
    "contact.branch.chat": "Chatee con {city} por WhatsApp",

    "contact.form.title": "Solicitar cotización",
    "contact.form.intro":
      "Complete sus datos una vez — luego envíe por WhatsApp o correo. Se enruta a la oficina que elija.",
    "contact.form.fullName": "Nombre completo",
    "contact.form.email": "Correo electrónico",
    "contact.form.phone": "Teléfono",
    "contact.form.message": "Cuéntenos sobre su viaje",
    "contact.form.messagePlaceholder":
      "Dos viajeros · Lilongwe → Dubái · 4–8 marzo · Clase ejecutiva · Hotel 5★ cerca de DIFC",
    "contact.form.branch": "Oficina preferida",
    "contact.form.branchAuto": "Automático — equipo más cercano",
    "contact.form.submit": "Enviar consulta",
    "contact.form.submitting": "Abriendo WhatsApp...",
    "contact.form.emailBtn": "Enviar por correo",
    "contact.form.whatsappBtn": "Enviar por WhatsApp",
    "contact.form.errNameEmailMessage":
      "Por favor, complete su nombre, correo y mensaje.",
    "contact.form.errValidEmail":
      "Por favor, introduzca un correo válido.",
    "contact.form.successWA":
      "Abriendo WhatsApp — su consulta está lista para enviar.",
    "contact.form.successEmail":
      "Abriendo su cliente de correo — su consulta está lista para enviar.",
    "contact.form.successDesc":
      "Respondemos en pocos minutos durante horario laboral.",

    "contact.upcoming.eyebrow": "Expandiendo Beyond Borders",
    "contact.upcoming.title": "Nuestras próximas oficinas.",
    "contact.upcoming.subtitle":
      "Futuras ubicaciones — oficinas planificadas, aún no operativas. Próximamente.",
    "contact.upcoming.badge": "Próximamente",
    "contact.upcoming.card": "Oficina próxima",
    "contact.upcoming.note":
      "Contacte cualquiera de nuestras oficinas actualmente operativas para consultas. Anunciaremos aquí las fechas de apertura de nuevas ubicaciones.",

    "contact.maps.eyebrow": "Encuéntrenos",
    "contact.maps.title": "Dos puertas. Una familia.",
    "contact.maps.titleAccent": "Una familia.",

    "page.services.eyebrow": "Nuestros servicios",
    "page.services.title1": "Ocho servicios.",
    "page.services.title2": "Un solo equipo de confianza.",
    "page.services.subtitle":
      "Cada detalle de su viaje — orquestado por un equipo que nunca cierra.",

    "page.destinations.eyebrow": "Destinos",
    "page.destinations.title1": "Inspiración para los",
    "page.destinations.title2": "grandes viajeros.",
    "page.destinations.subtitle":
      "Desde salas de juntas en Dubái hasta villas sobre el agua en Maldivas — nuestros destinos más solicitados, preseleccionados por el equipo.",

    "page.about.eyebrow": "Sobre nosotros",
    "page.about.title1": "Una oficina de viajes construida sobre la",
    "page.about.title2": "discreción.",

    "page.corporate.eyebrow": "Suite Corporativa",
    "page.corporate.title1": "Programas de viaje para",
    "page.corporate.title2": "empresas en crecimiento.",
  },
};

const LanguageContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (key) => key,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && TRANSLATIONS[stored]) return stored;
    return "en";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (TRANSLATIONS[next]) setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const table = TRANSLATIONS[lang] || TRANSLATIONS.en;
      let value = table[key];
      if (value === undefined) value = TRANSLATIONS.en[key];
      if (value === undefined) return key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          value = value.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        });
      }
      return value;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useTranslation = () => useContext(LanguageContext);
